import Link from 'next/link'
import Pill from '@/components/ui/Pill'
import RichText from '@/components/content/common/RichText'
import BlogIcon from '@/components/content/blog/shared/BlogIcon'
import AppIcon from '@/components/content/app/AppIcon'
import { FeaturedTagType } from '@/collections/FeaturedTag/constants'

import { Block } from '@/types/app'
import { getBrandColors, cn } from '@/lib/utils'
import type { App, Blog, BlogAppsApp } from '@/payload-types'

type BlogPost = Pick<
  Blog,
  'id' | 'name' | 'slug' | 'summary' | 'blogApps' | 'blogCategories' | 'createdAt'
>

type BlogThumbnailProps = {
  blog: BlogPost
  type?: string
  result?: boolean
}

export default function BlogThumbnail({ blog, type, result }: BlogThumbnailProps) {
  const { name, slug, summary } = blog

  const blogApps = blog.blogApps?.docs as BlogAppsApp[]
  const apps = blogApps.map((app) => app.app as App)
  const isNew = new Date(blog.createdAt) > new Date(Date.now() - 1000 * 60 * 60 * 24 * 30) // 30 days

  const link = `/blog/${slug}`

  if (type === FeaturedTagType.ThumbnailPrimary) {
    const SPEED = 20
    const APP_WIDTH = 48
    const GAP = 20

    const singleSetSize = apps.length * APP_WIDTH - (GAP - apps.length)
    const duration = singleSetSize / SPEED

    return (
      <Link
        href={link}
        className="group flex w-full flex-col gap-7 overflow-hidden rounded-2xl border border-gray-200 py-7 transition-colors hover:border-gray-300 dark:border-white/15"
      >
        <div className="relative my-4 h-12 shrink-0 overflow-hidden">
          <div
            className={cn(
              'flex items-center gap-5',
              apps?.length > 4
                ? 'pause group-hover:play absolute left-1/2 -translate-x-1/2 animate-marquee transition-[animation-play-state] duration-300'
                : 'px-6',
            )}
            style={
              {
                '--duration': `${duration}s`,
                '--single-set-size': `${singleSetSize}px`,
                '--offset-start': `calc(-50% + ${APP_WIDTH + GAP}px)`,
                '--offset-end': `calc(50% - ${APP_WIDTH + GAP}px)`,
              } as React.CSSProperties
            }
          >
            <>
              {apps.map((app, index) => {
                const { backgroundColor } = getBrandColors(app)
                return (
                  <AppIcon
                    key={`original-${index}`}
                    icon={app.icon}
                    glyph={app.glyph}
                    name={app.name}
                    background={backgroundColor}
                    size={48}
                  />
                )
              })}
              {apps?.length > 4 &&
                apps.map((app, index) => {
                  const { backgroundColor } = getBrandColors(app)
                  return (
                    <AppIcon
                      key={`duplicate-${index}`}
                      icon={app.icon}
                      glyph={app.glyph}
                      name={app.name}
                      background={backgroundColor}
                      size={48}
                    />
                  )
                })}
            </>
          </div>
        </div>

        <div className="flex items-center gap-3.5 px-6">
          {isNew && <Pill style="secondary">New!</Pill>}
          <div className="text-13 text-gray-400">
            <span className="text-gray-600">3 min read</span>, {apps?.length} featured apps
          </div>
        </div>
        <div className="flex h-full flex-col gap-5 px-6">
          <div className="flex flex-col gap-3">
            <h2 className="text-gray-950 dark:text-white">{name}</h2>
            <div className="text-14 text-gray-600/60 dark:text-white/60">
              <RichText
                content={summary.root.children as Block[]}
                truncate={true}
                maxLength={150}
              />
            </div>
          </div>

          <Pill style="primary" className="mt-auto">
            Read Post
          </Pill>
        </div>
      </Link>
    )
  } else if (type === FeaturedTagType.ThumbnailSecondary) {
    return (
      <div className="flex w-full flex-col items-start gap-5">
        <figure className="relative aspect-video h-auto w-full overflow-hidden rounded-2xl bg-black"></figure>
        <div className="flex flex-col gap-4 px-2">
          <div className="flex flex-col gap-1">
            <div className="text-18 font-semibold text-gray-950">{name}</div>
            <div className="text-gray-600/60">
              <RichText content={summary.root.children as Block[]} />
            </div>
          </div>

          <Link href={link}>
            <Pill style="primary">Read Post</Pill>
          </Link>
        </div>
      </div>
    )
  } else {
    return (
      <Link
        href={link}
        key={blog.id}
        className={cn(
          'group flex items-center justify-between gap-5 md:gap-10',
          result && 'rounded-xl border border-gray-200 p-3',
        )}
      >
        <div className="flex w-full items-center gap-5 md:w-[calc(100%-90px)]">
          <BlogIcon apps={apps} />

          <div className={cn('flex flex-col', result ? 'gap-1' : 'mt-1')}>
            <div className="flex h-5 w-full items-center gap-2.5">
              <span
                className={cn(
                  'line-clamp-1 font-semibold text-gray-950',
                  result ? 'text-14' : 'text-16',
                )}
              >
                {blog.name}
              </span>
            </div>
            <div
              aria-hidden="true"
              className={cn('line-clamp-1 w-full text-gray-400', result ? 'text-12' : 'text-13')}
            >
              <span className="text-gray-600">3 min read</span>, {blog.blogApps?.docs?.length}{' '}
              featured apps
            </div>
          </div>
        </div>

        <Pill as="span" style="outlinePrimary" className="ml-auto hidden sm:flex">
          See
        </Pill>
      </Link>
    )
  }
}
